import { Sun, Moon } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

/**
 * Compact night-mode toggle button.
 *
 * Props:
 *   - variant: "icon" (default) | "pill"
 *   - className: optional extra classes
 */
export default function ThemeToggle({ variant = "icon", className = "" }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={isDark ? "Switch to light mode" : "Switch to night mode"}
        className={`h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-bold transition-colors ${
          isDark
            ? "bg-slate-700 text-amber-300 hover:bg-slate-600"
            : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
        } ${className}`}
        data-testid="theme-toggle-btn"
      >
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        <span>{isDark ? "Light Mode" : "Night Mode"}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to night mode"}
      title={isDark ? "Switch to light mode" : "Switch to night mode"}
      className={`h-11 w-11 sm:h-9 sm:w-9 rounded-xl sm:rounded-md grid place-items-center transition-colors ${
        isDark
          ? "text-amber-300 hover:bg-slate-700/60 active:bg-slate-700"
          : "text-slate-600 hover:bg-slate-100 active:bg-slate-200"
      } ${className}`}
      data-testid="theme-toggle-btn"
    >
      {isDark ? <Sun className="w-5 h-5 sm:w-4 sm:h-4" /> : <Moon className="w-5 h-5 sm:w-4 sm:h-4" />}
    </button>
  );
}
